let serial = 0;
const impression = 'M8 16Q30 1 56 14Q77 33 91 21Q98 53 86 88Q58 99 15 86Q0 55 8 16Z';

function ink(ctx, host, kit, value, x, y, size, mirrorX = false, mirrorY = false) {
  const scale = size / 100;
  const placement = kit.add(ctx, host, 'g', {
    transform: `translate(${x + (mirrorX ? size : 0)} ${y + (mirrorY ? size : 0)}) scale(${mirrorX ? -scale : scale} ${mirrorY ? -scale : scale})`,
  });
  const mark = kit.add(ctx, placement, 'g', { 'data-paper-mark': String(value) });
  kit.path(ctx, mark, impression, { fill: `var(--game-colour-${value})`, opacity: 0.88 });
  kit.path(ctx, mark, 'M17 26Q40 15 70 36', {
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 2,
    opacity: 0.2,
  });
  return mark;
}

/** The editable source quarter exposes stored ink only. Paper fibres and edge
 * lighting are original procedural decoration; no per-cell fold is invented. */
export function paperQuarterArtwork(ctx, cell, kit, shape) {
  const art = kit.svg(ctx, 'game-piece-art creative-paper-tile');
  kit.add(ctx, art, 'rect', {
    x: 1,
    y: 1,
    width: 98,
    height: 98,
    rx: 2,
    fill: 'var(--game-paper)',
    stroke: 'var(--game-paper-muted)',
    'stroke-opacity': 0.25,
  });
  for (const y of [17, 67])
    kit.path(ctx, art, `M11 ${y}L19 ${y - 1}`, {
      fill: 'none',
      stroke: 'var(--game-paper-muted)',
      opacity: 0.13,
      'stroke-width': 0.6,
    });
  if (Number.isInteger(cell.value) && cell.value >= 0 && cell.value < 6) {
    ink(ctx, art, kit, cell.value, 1, 1, 98);
    const guide = kit.add(ctx, art, 'g', {
      transform: 'translate(65 66) scale(.25)',
      opacity: 0.5,
    });
    kit.symbol(ctx, guide, shape, 'var(--game-night)');
  }
  return art;
}

/** Render the domain-projected64-cell print exactly; never calculate reflection
 * membership or edit the source. Local impression orientation mirrors each
 * already authorized quadrant. Stable panel groups support causal unfolding. */
export function paperSceneArtwork(ctx, art, scene, kit) {
  art.setAttribute('class', 'game-scene-art creative-world creative-paper-world');
  art.setAttribute('data-paper-horizontal', String(Boolean(scene.horizontal)));
  art.setAttribute('data-paper-vertical', String(Boolean(scene.vertical)));
  const id = `paper-grain-${++serial}`;
  const defs = kit.add(ctx, art, 'defs', {});
  const paint = kit.add(ctx, defs, 'linearGradient', {
    id,
    x1: '0%',
    y1: '0%',
    x2: '100%',
    y2: '100%',
  });
  const paper =
    scene.paper === 1
      ? 'color-mix(in srgb,var(--game-white) 96%,var(--game-colour-4))'
      : scene.paper === 2
        ? 'color-mix(in srgb,var(--game-white) 96%,var(--game-colour-5))'
        : 'var(--game-paper)';
  for (const [offset, fill] of [
    ['0%', 'var(--game-white)'],
    ['65%', paper],
    ['100%', 'color-mix(in srgb,var(--game-paper) 94%,var(--game-night))'],
  ])
    kit.add(ctx, paint, 'stop', { offset, 'stop-color': fill });
  kit.add(ctx, art, 'rect', {
    x: 9,
    y: 11,
    width: 344,
    height: 344,
    rx: 7,
    fill: 'var(--game-night)',
    opacity: 0.25,
  });
  kit.add(ctx, art, 'rect', { x: 7, y: 7, width: 346, height: 346, rx: 6, fill: `url(#${id})` });
  const quadrants = new Map();
  for (const [name, x, y] of [
    ['tl', 8, 8],
    ['tr', 180, 8],
    ['bl', 8, 180],
    ['br', 180, 180],
  ]) {
    const group = kit.add(ctx, art, 'g', { 'data-paper-quadrant': name });
    kit.add(ctx, group, 'rect', {
      x,
      y,
      width: 172,
      height: 172,
      fill: 'var(--game-white)',
      opacity: 0.025,
    });
    quadrants.set(name, group);
  }
  const print = Array.isArray(scene.print) && scene.print.length === 64 ? scene.print : [];
  for (const [index, value] of print.entries()) {
    if (!Number.isInteger(value) || value < 0 || value >= 6) continue;
    const column = index % 8;
    const row = Math.floor(index / 8);
    const right = column >= 4;
    const bottom = row >= 4;
    const cell = kit.add(ctx, quadrants.get(`${bottom ? 'b' : 't'}${right ? 'r' : 'l'}`), 'g', {
      'data-paper-cell': String(index),
    });
    ink(
      ctx,
      cell,
      kit,
      value,
      8 + column * 43,
      8 + row * 43,
      43,
      right && scene.vertical,
      bottom && scene.horizontal,
    );
  }
  for (let i = 0; i < 12; i++) {
    const x = 21 + (i % 4) * 83;
    const y = 20 + Math.floor(i / 4) * 110;
    kit.path(ctx, art, `M${x} ${y}l${5 + (i % 3)} ${i % 2 ? -1 : 1}`, {
      fill: 'none',
      stroke: 'var(--game-paper-muted)',
      opacity: 0.14,
      'stroke-width': 0.7,
    });
  }
  for (const [d, stroke, width, opacity] of [
    ['M180 8V352M8 180H352', 'var(--game-night)', 2.5, 0.14],
    ['M178 8V352M8 178H352', 'var(--game-white)', 1.3, 0.8],
    ['M12 8H348M8 13V348', 'var(--game-white)', 1.2, 0.65],
  ])
    kit.path(ctx, art, d, { fill: 'none', stroke, 'stroke-width': width, opacity });
}
